import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall, normalizeApiError } from "../utils/apiResponseHandler";

export interface CreateAgencyRequest {
  name: string;
  description?: string;
  contact_email?: string;
  contactEmail?: string; // Alternative naming for consistency
  parentAgencyId?: string; // Support for sub-agency creation
}

export interface Agency {
  id: string;
  name: string;
  description?: string;
  contact_email?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  owner_id?: string;
  owner_role?: string;
  permissions?: string[];
}

export interface CreateAgencyResponse {
  success: boolean;
  data: {
    agency: Agency;
    user_role: string;
  };
  message?: string;
}

export interface AgencyWithSubAgencies {
  id: string;
  name: string;
  description?: string;
  contactEmail?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  role: string;
  permissions: string[];
  agencyRole: string;
  parentAgencyId?: string;
  subAgencies?: AgencyWithSubAgencies[];
}

export interface AgencyHierarchyResponse {
  items: AgencyWithSubAgencies[];
}

export const agencyService = {
  // Get agencies with hierarchy (main agencies with sub-agencies grouped)
  getAgenciesHierarchy: async (): Promise<AgencyHierarchyResponse> => {
    try {
      const result = await handleApiCall<unknown>(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.agency.getAgenciesHierarchy),
          auth: true // Add authentication
        });
        return response;
      });
      
      // Ensure we have a valid response structure
      if (!result) {

        return { items: [] };
      }
      
      const data = result as any;

      // Handle standardized response format: { statusCode, message, data: { items[], meta{} }, timestamp }
      if (data?.data?.items && Array.isArray(data.data.items)) {
        return { items: data.data.items };
      }

      // Legacy format: { data: [...] }
      if (data && data.data && Array.isArray(data.data)) {
        return { items: data.data };
      }

      // Fallback: data directly as array
      if (Array.isArray(data)) {
        return { items: data };
      }
      
      // Fallback for wrapped response (legacy fallback)
      if (data.items && Array.isArray(data.items)) {
        return { items: data.items };
      }

      return { items: [] };
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to fetch agencies hierarchy') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  },

  // Create a new agency for independent users
  createAgency: async (data: CreateAgencyRequest): Promise<CreateAgencyResponse> => {
    try {
      const res = await handleApiCall<unknown>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.agency.createForIndependent),
          body: data,
          auth: true // Add authentication
        });
      });
      const payload = res as any;
      return (payload?.data ?? payload) as CreateAgencyResponse;
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to create agency') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  },

  // Create a new independent or sub-agency (unified method)
  createIndependentAgency: async (data: CreateAgencyRequest & { parentAgencyId?: string }): Promise<CreateAgencyResponse> => {
    try {
      // Send data with camelCase field names as expected by backend DTO
      const requestData = {
        name: data.name,
        description: data.description,
        contactEmail: data.contactEmail || data.contact_email,
        parentAgencyId: data.parentAgencyId
      };

      const res = await handleApiCall<unknown>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.agency.createForIndependent),
          body: requestData,
          auth: true
        });
      });
      const payload = res as any;
      return (payload?.data ?? payload) as CreateAgencyResponse;
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to create agency') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  },

  // Get agency details
  getAgency: async (agencyId: string): Promise<Agency> => {
    try {
      const res = await handleApiCall<unknown>(async () => {
        return await api.get({ 
          url: getApiUrl(endpoint.agency.getAgencyById(agencyId)),
          auth: true // Add authentication
        });
      });
      const payload = res as any;
      return (payload?.data ?? payload) as Agency;
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to load agency details') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  },

  // Update agency
  updateAgency: async (agencyId: string, data: Partial<CreateAgencyRequest>): Promise<Agency> => {
    try {
      const res = await handleApiCall<unknown>(async () => {
        return await api.put({ 
          url: getApiUrl(endpoint.agency.updateAgency(agencyId)), 
          body: data,
          auth: true // Add authentication
        });
      });
      const payload = res as any;
      return (payload?.data ?? payload) as Agency;
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to update agency') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  },

  // Get user's agency
  getUserAgency: async (): Promise<Agency | null> => {
    try {
      const res = await handleApiCall<unknown>(async () => {
        return await api.get({ 
          url: getApiUrl(endpoint.agency.getAgencies),
          auth: true // Add authentication
        });
      });
      const responseData = res as any;
      let agencies: Agency[];
      
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        agencies = responseData.data as Agency[];
      } else if (Array.isArray(responseData)) {
        agencies = responseData as Agency[];
      } else {
        return null;
      }

      // Assuming the first agency is the user's agency
      return agencies[0] || null;
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to fetch user agency') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  },

  // Check if user can create agency
  canCreateAgency: async (): Promise<{ can_create: boolean; reason?: string; requires_premium?: boolean }> => {
    try {
      const res = await handleApiCall<unknown>(async () => {
        return await api.get({
          url: getApiUrl(endpoint.agency.canCreate),
          auth: true // Add authentication
        });
      });
      const payload = res as any;
      return (payload?.data ?? payload) as { can_create: boolean; reason?: string; requires_premium?: boolean };
    } catch (error) {
      const n = normalizeApiError(error);
      const e = new Error(n.message || 'Failed to check agency creation eligibility') as Error & { code?: string | number; retryAfterSeconds?: number };
      e.code = n.code;
      e.retryAfterSeconds = n.retryAfterSeconds;
      throw e;
    }
  }
};

export default agencyService;
